// ─────────────────────────────────────────────────────────────────────────
// Libro contable (26-ago-2026, Fase G) — diario, mayor y BORRADOR del F29.
// El botón "Generar" corre el motor de asientos: deriva la contabilidad de
// los documentos existentes (facturas, rendiciones, cartola). Idempotente:
// correrlo de nuevo no duplica nada. Diseño: agente contador (mapeo §4).
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";

const fmt = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
};
const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const fmt0 = n => (n ? fmt(n) : "");            // celdas de tabla: $0 se ve vacío
// R3: cortar en coma — "5.200.000,50" pegado del Excel es $5.200.000, no ×100.
const parseMonto = s => Number(String(s ?? "").split(",")[0].replace(/[^\d-]/g, "")) || 0;
// 28-ago: fontSize 13 parejo en todos los inputs de mes/fecha de la pestaña
const inputS = { background: "#161616", border: "1px solid #333", borderRadius: 8, color: "#ddd", padding: "8px 10px", fontSize: 13, colorScheme: "dark" };

// ── CSV en el cliente (28-ago): descarga lo visible como .csv (Excel Chile: ";")
const bajarCsv = (nombre, filas) => {
  const esc = c => {
    const s = c === null || c === undefined ? "" : String(c);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = "\uFEFF" + filas.map(f => f.map(esc).join(";")).join("\r\n"); // BOM: Excel abre el UTF-8 bien
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const btnCsv = {
  background: "#1e1e1e", color: "#3498db", border: "1px solid #1a4a6e",
  borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700,
  cursor: "pointer", whiteSpace: "nowrap", marginLeft: "auto",
};
const th = { padding: "6px 8px", fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".04em", textAlign: "right", whiteSpace: "nowrap", borderBottom: "1px solid #333" };
const td = { padding: "5px 8px", fontSize: 12, color: "#ddd", textAlign: "right", borderBottom: "1px solid #282828", whiteSpace: "nowrap", ...num };

const Libro = () => {
  // Pulido desktop 27-ago: hover en filas del balance (solo desktop). Los
  // montos ya van a la derecha con tabular-nums y el balance trae su propio
  // overflow-x, que en desktop ocupa todo el ancho de la pestaña.
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState(mesActual());
  const [vista, setVista] = useState("f29"); // f29 | mayor | diario
  const [f29, setF29] = useState(null);
  const [mayor, setMayor] = useState([]);
  const [diario, setDiario] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  // Estados financieros + apertura + excepciones (27-ago)
  const [excepciones, setExcepciones] = useState([]);
  const [balance, setBalance] = useState(null);
  const [hastaBal, setHastaBal] = useState(mesActual());
  const [eerr, setEerr] = useState(null);
  const [eerrDesde, setEerrDesde] = useState(`${new Date().getFullYear()}-01`);
  const [eerrHasta, setEerrHasta] = useState(mesActual());
  const [apGuardadas, setApGuardadas] = useState([]);
  const [apFecha, setApFecha] = useState("2026-01-01");
  const [apLineas, setApLineas] = useState([]);
  const [apPaste, setApPaste] = useState("");
  const [apMsg, setApMsg] = useState(null);
  const [apGuardando, setApGuardando] = useState(false);

  const cargar = async (p = periodo) => {
    try {
      const [rf, rm, rd, rx] = await Promise.all([
        backend.get("libro/f29", { params: { periodo: p } }),
        backend.get("libro/mayor", { params: { periodo: p } }),
        backend.get("libro/diario", { params: { periodo: p } }),
        backend.get("libro/excepciones", { params: { periodo: p } }),
      ]);
      setF29(rf.data); setMayor(rm.data || []); setDiario(rd.data || []);
      setExcepciones(rx.data || []);
      setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };
  useEffect(() => { cargar(periodo); /* eslint-disable-next-line */ }, [periodo]);

  useEffect(() => {
    if (vista !== "balance" || !/^\d{4}-\d{2}$/.test(hastaBal)) return;
    backend.get("libro/balance", { params: { hasta: hastaBal } })
      .then(r => setBalance(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, [vista, hastaBal]);

  useEffect(() => {
    if (vista !== "eerr" || !/^\d{4}-\d{2}$/.test(eerrDesde) || !/^\d{4}-\d{2}$/.test(eerrHasta)) return;
    backend.get("libro/eerr", { params: { desde: eerrDesde, hasta: eerrHasta } })
      .then(r => setEerr(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, [vista, eerrDesde, eerrHasta]);

  const cargarApertura = async () => {
    try {
      const r = await backend.get("libro/apertura");
      setApGuardadas(r.data || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };
  useEffect(() => { if (vista === "apertura") cargarApertura(); /* eslint-disable-next-line */ }, [vista]);

  // Pegado desde la planilla del contador: una línea por cuenta, columnas
  // separadas por TAB o ";" → cuenta | nombre… | debe | haber.
  const cargarPegado = () => {
    const nuevas = apPaste.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
      const cols = l.split(/\t|;/).map(c => c.trim());
      if (cols.length < 3) return null;
      return {
        cuenta: cols[0],
        nombre: cols.slice(1, -2).join(" "),
        debe: parseMonto(cols[cols.length - 2]),
        haber: parseMonto(cols[cols.length - 1]),
      };
    }).filter(Boolean);
    if (!nuevas.length) { setApMsg({ tipo: "warn", texto: "No se reconoció ninguna línea. Formato: cuenta [TAB o ;] nombre [TAB o ;] debe [TAB o ;] haber." }); return; }
    setApLineas([...apLineas, ...nuevas]);
    setApPaste("");
    setApMsg({ tipo: "ok", texto: `${nuevas.length} líneas cargadas al formulario — revisa y toca Guardar.` });
  };

  const guardarApertura = async (permitirDescuadre = false) => {
    const lineas = apLineas.filter(l => l.cuenta && (parseMonto(l.debe) > 0 || parseMonto(l.haber) > 0))
      .map(l => ({ cuenta: l.cuenta, nombreCuenta: l.nombre, debe: parseMonto(l.debe), haber: parseMonto(l.haber) }));
    if (!lineas.length) { setApMsg({ tipo: "warn", texto: "No hay líneas con cuenta y monto para guardar." }); return; }
    setApGuardando(true); setApMsg(null);
    try {
      const r = await backend.post("libro/apertura", { fecha: apFecha, origen: "balance_contador", lineas, permitirDescuadre });
      setApMsg(r.data.warning
        ? { tipo: "warn", texto: `Guardadas ${r.data.lineas} líneas. ⚠ ${r.data.warning}` }
        : { tipo: "ok", texto: `Guardadas ${r.data.lineas} líneas — apertura cuadrada (D ${fmt(r.data.totalDebe)} = H ${fmt(r.data.totalHaber)}).` });
      setApLineas([]);
      await cargarApertura();
    } catch (e) {
      const d = e?.response?.data;
      // C18 — si el backend rechaza por descuadre, ofrecer guardar igual (override
      // explícito) con confirmación; el resto de errores se muestran normal.
      if (d?.error === "apertura_descuadrada" && !permitirDescuadre) {
        setApGuardando(false);
        if (window.confirm(`${d.message}\n\n¿Guardar descuadrada de todas formas?`)) return guardarApertura(true);
        setApMsg({ tipo: "warn", texto: d.message });
        return;
      }
      setApMsg({ tipo: "error", texto: d?.message || d?.error || e.message });
    }
    setApGuardando(false);
  };

  const borrarLineaApertura = async (l) => {
    try {
      await backend.delete("libro/apertura", { params: { fecha: String(l.fecha).slice(0, 10), cuenta: l.cuenta } });
      await cargarApertura();
    } catch (e) { setApMsg({ tipo: "error", texto: e?.response?.data?.error || e.message }); }
  };

  const generar = async () => {
    setGenerando(true); setResultado(null); setError(null);
    try {
      const r = await backend.post("libro/generar", { periodo });
      setResultado(r.data);
      await cargar();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    setGenerando(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)} style={inputS} />
        <button onClick={generar} disabled={generando} className="t-02"
          style={{ background: generando ? "#333" : "#e30613", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 40 }}>
          {generando ? "Generando…" : "⚙ Generar asientos del período"}
        </button>
        <div className="chips-scroll" style={{ display: "flex", gap: 6 }}>
          {[
            ["f29", "F29 borrador"], ["mayor", "Mayor"], ["diario", `Diario (${diario.length})`],
            ["balance", "Balance"], ["eerr", "Resultados"], ["apertura", "Apertura"],
            ["excepciones", excepciones.length ? `Excepciones (${excepciones.length})` : "Excepciones"],
          ].map(([k, l]) => (
            <button key={k} type="button" className="chip-touch no-touch-min t-02" onClick={() => setVista(k)}
              style={{ background: vista === k ? "#3a1114" : "#1e1e1e", color: vista === k ? "#ff8a8f" : "#999",
                border: `1px solid ${vista === k ? "#e30613" : "#333"}`, borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={{ color: "#e74c3c", fontSize: 12 }}>{error}</div>}
      {resultado && (
        <div style={{ ...S.card, borderColor: "#0d4020", fontSize: 12, color: "#b8b8b8" }}>
          ✓ Motor: <b style={{ color: "#2ecc71" }}>{resultado.creados} asientos nuevos</b> · {resultado.yaExistian} ya existían ·{" "}
          <span style={{ color: resultado.excepciones?.length ? "#f39c12" : "#8a8a8a" }}>{resultado.excepciones?.length || 0} excepciones</span>
          {resultado.cierre && !resultado.cierre.sinMovimientos && (
            <span> · Cierre IVA: {resultado.cierre.aPagar > 0 ? <b style={{ color: "#f39c12" }}>a pagar {fmt(resultado.cierre.aPagar)}</b> : <b style={{ color: "#2ecc71" }}>remanente {fmt(resultado.cierre.remanenteNuevo)}</b>}</span>
          )}
          {resultado.excepciones?.slice(0, 5).map((x, i) => (
            <div key={i} style={{ fontSize: 11, color: "#f39c12", marginTop: 4 }}>⚠ {x.glosa}: {x.motivo}</div>
          ))}
        </div>
      )}

      {/* ── F29 borrador ── */}
      {vista === "f29" && f29 && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={S.label}>F29 BORRADOR — {periodo} (revisar antes de declarar)</div>
            <button type="button" className="t-02" style={btnCsv}
              onClick={() => bajarCsv(`f29_${periodo}.csv`,
                [["Código", "Concepto", "Monto"], ...f29.lineas.map(l => [l.codigo, l.nombre, l.monto ?? ""])])}>
              ⬇ CSV
            </button>
          </div>
          {f29.lineas.map(l => (
            <div key={l.codigo} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", borderBottom: "1px solid #282828" }}>
              <span style={{ fontSize: 12, color: "#b8b8b8" }}>
                <span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, marginRight: 8 }}>[{l.codigo}]</span>
                {l.nombre}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, ...num,
                color: l.codigo === "TOTAL" ? "#e30613"
                  : l.codigo === "91" ? (l.monto > 0 ? "#f39c12" : "#2ecc71") : "#fff" }}>
                {l.monto == null ? "—" : fmt(l.monto)}
              </span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 10 }}>{f29.nota}</div>
        </div>
      )}

      {/* ── Mayor ── */}
      {vista === "mayor" && (
        <div style={S.card}>
          <div style={{ ...S.label, marginBottom: 8 }}>Libro mayor — {periodo}</div>
          {mayor.length === 0 && <div style={{ fontSize: 12, color: "#8a8a8a" }}>Sin asientos — toca "Generar asientos del período".</div>}
          {mayor.map(c => (
            <div key={c.cuenta} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "7px 0", borderBottom: "1px solid #282828", flexWrap: "wrap" }}>
              <span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, flexShrink: 0 }}>{c.cuenta}</span>
              <span style={{ flex: 1, minWidth: 120, fontSize: 12, color: "#fff" }}>{c.nombre}</span>
              <span style={{ fontSize: 11, color: "#8a8a8a", ...num }}>D {fmt(c.debe)} · H {fmt(c.haber)}</span>
              <span style={{ fontSize: 13, fontWeight: 700, ...num, color: c.saldo >= 0 ? "#2ecc71" : "#e74c3c", minWidth: 90, textAlign: "right" }}>{fmt(c.saldo)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Diario ── */}
      {vista === "diario" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {diario.length === 0 && <div style={{ ...S.card, fontSize: 12, color: "#8a8a8a" }}>Sin asientos — toca "Generar asientos del período".</div>}
          {diario.map(a => (
            <div key={a._id} style={{ ...S.card, padding: "10px 14px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontFamily: "monospace", color: "#f39c12", fontWeight: 700, fontSize: 11 }}>#{a.numero}</span>
                <span style={{ flex: 1, minWidth: 140, fontSize: 12, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.glosa}</span>
                <span style={{ fontSize: 10, color: "#8a8a8a" }}>{new Date(a.fecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}</span>
              </div>
              <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: "2px solid #2e2e2e" }}>
                {a.lineas.map((l, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0", ...num }}>
                    <span style={{ color: "#b8b8b8", paddingLeft: l.haber > 0 ? 18 : 0 }}>
                      <span style={{ fontFamily: "monospace", color: "#8a8a8a" }}>{l.cuenta}</span> {l.nombreCuenta}
                    </span>
                    <span style={{ color: l.debe > 0 ? "#fff" : "#8a8a8a" }}>{l.debe > 0 ? fmt(l.debe) : fmt(l.haber)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Balance de 8 columnas ── */}
      {vista === "balance" && (
        <div style={S.card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <div style={S.label}>Balance de 8 columnas — hasta</div>
            <input type="month" value={hastaBal} onChange={e => setHastaBal(e.target.value)} style={inputS} />
            {balance && (
              <span style={{ fontSize: 11, color: balance.cuadrado ? "#2ecc71" : "#f39c12" }}>
                {balance.cuadrado ? "✓ cuadrado" : "⚠ descuadrado — revisa la apertura"}
                {balance.aperturaLineas > 0 ? ` · apertura: ${balance.aperturaLineas} líneas` : " · sin apertura cargada"}
              </span>
            )}
            {balance && balance.cuentas.length > 0 && (
              <button type="button" className="t-02" style={btnCsv}
                onClick={() => bajarCsv(`balance_${hastaBal}.csv`, [
                  ["Cuenta", "Nombre", "Debe", "Haber", "S. deudor", "S. acreedor", "Activo", "Pasivo", "Pérdida", "Ganancia"],
                  ...balance.cuentas.map(c => [c.cuenta, c.nombre, c.debe || 0, c.haber || 0, c.saldoDeudor || 0, c.saldoAcreedor || 0, c.activo || 0, c.pasivo || 0, c.perdida || 0, c.ganancia || 0]),
                  ...(balance.cuadre ? [["", balance.cuadre.nombre, "", "", "", "", balance.cuadre.activo || 0, balance.cuadre.pasivo || 0, balance.cuadre.perdida || 0, balance.cuadre.ganancia || 0]] : []),
                  ["", "TOTALES", balance.totales.debe, balance.totales.haber, balance.totales.saldoDeudor, balance.totales.saldoAcreedor, balance.totales.activo, balance.totales.pasivo, balance.totales.perdida, balance.totales.ganancia],
                ])}>
                ⬇ CSV
              </button>
            )}
          </div>
          {!balance && <div style={{ fontSize: 12, color: "#8a8a8a" }}>Cargando…</div>}
          {balance && balance.cuentas.length === 0 && <div style={{ fontSize: 12, color: "#8a8a8a" }}>Sin movimientos hasta {hastaBal}.</div>}
          {balance && balance.cuentas.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", minWidth: 980, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: "left" }}>Cuenta</th>
                    <th style={{ ...th, textAlign: "left" }}>Nombre</th>
                    <th style={th}>Debe</th><th style={th}>Haber</th>
                    <th style={th}>S. deudor</th><th style={th}>S. acreedor</th>
                    <th style={th}>Activo</th><th style={th}>Pasivo</th>
                    <th style={th}>Pérdida</th><th style={th}>Ganancia</th>
                  </tr>
                </thead>
                <tbody>
                  {balance.cuentas.map(c => (
                    <tr key={c.cuenta}
                      onMouseEnter={e => { if (!isMobile) Array.from(e.currentTarget.cells).forEach(x => x.style.background = "#2a2a2a"); }}
                      onMouseLeave={e => { if (!isMobile) Array.from(e.currentTarget.cells).forEach(x => x.style.background = ""); }}>
                      <td style={{ ...td, textAlign: "left", fontFamily: "monospace", color: "#3498db", fontWeight: 700 }}>{c.cuenta}</td>
                      <td style={{ ...td, textAlign: "left", color: "#fff", whiteSpace: "normal", minWidth: 160 }}>{c.nombre}</td>
                      <td style={td}>{fmt0(c.debe)}</td><td style={td}>{fmt0(c.haber)}</td>
                      <td style={td}>{fmt0(c.saldoDeudor)}</td><td style={td}>{fmt0(c.saldoAcreedor)}</td>
                      <td style={td}>{fmt0(c.activo)}</td><td style={td}>{fmt0(c.pasivo)}</td>
                      <td style={td}>{fmt0(c.perdida)}</td><td style={td}>{fmt0(c.ganancia)}</td>
                    </tr>
                  ))}
                  {balance.cuadre && (
                    <tr>
                      <td style={{ ...td, textAlign: "left" }}></td>
                      <td style={{ ...td, textAlign: "left", fontStyle: "italic", color: balance.resultadoEjercicio >= 0 ? "#2ecc71" : "#e74c3c" }}>{balance.cuadre.nombre}</td>
                      <td style={td}></td><td style={td}></td><td style={td}></td><td style={td}></td>
                      <td style={td}>{fmt0(balance.cuadre.activo)}</td><td style={td}>{fmt0(balance.cuadre.pasivo)}</td>
                      <td style={td}>{fmt0(balance.cuadre.perdida)}</td><td style={td}>{fmt0(balance.cuadre.ganancia)}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ ...td, textAlign: "left", borderTop: "2px solid #444", fontWeight: 700, color: "#fff" }} colSpan={2}>TOTALES</td>
                    {["debe", "haber", "saldoDeudor", "saldoAcreedor", "activo", "pasivo", "perdida", "ganancia"].map(k => (
                      <td key={k} style={{ ...td, borderTop: "2px solid #444", fontWeight: 700, color: "#fff" }}>{fmt(balance.totales[k])}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Estado de resultados ── */}
      {vista === "eerr" && (
        <div style={S.card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <div style={S.label}>Estado de resultados</div>
            <input type="month" value={eerrDesde} onChange={e => setEerrDesde(e.target.value)} style={inputS} />
            <span style={{ color: "#8a8a8a", fontSize: 12 }}>→</span>
            <input type="month" value={eerrHasta} onChange={e => setEerrHasta(e.target.value)} style={inputS} />
            {eerr && (
              <button type="button" className="t-02" style={btnCsv}
                onClick={() => bajarCsv(`eerr_${eerrDesde}_a_${eerrHasta}.csv`, [
                  ["Tipo", "Cuenta", "Nombre", "Monto"],
                  ...eerr.ingresos.map(x => ["Ingreso", x.cuenta, x.nombre, x.monto]),
                  ["", "", "Total ingresos", eerr.totalIngresos],
                  ...eerr.gastos.map(x => ["Gasto", x.cuenta, x.nombre, x.monto]),
                  ["", "", "Total gastos", eerr.totalGastos],
                  ["", "", eerr.resultado >= 0 ? "Utilidad del período" : "Pérdida del período", eerr.resultado],
                ])}>
                ⬇ CSV
              </button>
            )}
          </div>
          {!eerr && <div style={{ fontSize: 12, color: "#8a8a8a" }}>Cargando…</div>}
          {eerr && (
            <div>
              <div style={{ ...S.label, color: "#2ecc71", margin: "6px 0" }}>Ingresos</div>
              {eerr.ingresos.length === 0 && <div style={{ fontSize: 12, color: "#8a8a8a", padding: "4px 0" }}>Sin ingresos en el rango.</div>}
              {eerr.ingresos.map(i => (
                <div key={i.cuenta} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #282828", fontSize: 12 }}>
                  <span style={{ color: "#b8b8b8" }}><span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, marginRight: 8 }}>{i.cuenta}</span>{i.nombre}</span>
                  <span style={{ ...num, color: "#fff" }}>{fmt(i.monto)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13, fontWeight: 700 }}>
                <span style={{ color: "#2ecc71" }}>Total ingresos</span>
                <span style={{ ...num, color: "#2ecc71" }}>{fmt(eerr.totalIngresos)}</span>
              </div>

              <div style={{ ...S.label, color: "#e74c3c", margin: "10px 0 6px" }}>Gastos</div>
              {eerr.gastos.length === 0 && <div style={{ fontSize: 12, color: "#8a8a8a", padding: "4px 0" }}>Sin gastos en el rango.</div>}
              {eerr.gastos.map(g => (
                <div key={g.cuenta} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #282828", fontSize: 12 }}>
                  <span style={{ color: "#b8b8b8" }}><span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, marginRight: 8 }}>{g.cuenta}</span>{g.nombre}</span>
                  <span style={{ ...num, color: "#fff" }}>{fmt(g.monto)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13, fontWeight: 700 }}>
                <span style={{ color: "#e74c3c" }}>Total gastos</span>
                <span style={{ ...num, color: "#e74c3c" }}>{fmt(eerr.totalGastos)}</span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", borderTop: "2px solid #444", fontSize: 15, fontWeight: 700 }}>
                <span style={{ color: "#fff" }}>{eerr.resultado >= 0 ? "Utilidad del período" : "Pérdida del período"}</span>
                <span style={{ ...num, color: eerr.resultado >= 0 ? "#2ecc71" : "#e74c3c" }}>{fmt(eerr.resultado)}</span>
              </div>
              <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 8 }}>{eerr.nota}</div>
            </div>
          )}
        </div>
      )}

      {/* ── Saldos de apertura ── */}
      {vista === "apertura" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ ...S.card, borderColor: "#5a4a12", fontSize: 12, color: "#f0d060" }}>
            💡 Trae el balance del contador al 31-dic-2025 para llenar esto; mientras, la app opera desde 2026 en cero.
            La apertura parcial es válida — se puede ir completando por partes.
          </div>

          <div style={S.card}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
              <div style={S.label}>Nueva carga — fecha de corte</div>
              <input type="date" value={apFecha} onChange={e => setApFecha(e.target.value)} style={inputS} />
            </div>

            <textarea value={apPaste} onChange={e => setApPaste(e.target.value)} rows={4}
              placeholder={"Pega acá las líneas del balance (una por fila):\ncuenta ; nombre ; debe ; haber\n1102 ; Banco Santander ; 5.200.000 ; 0"}
              style={{ ...inputS, width: "100%", boxSizing: "border-box", fontSize: 12, fontFamily: "monospace", resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              <button type="button" onClick={cargarPegado} className="t-02"
                style={{ background: "#1e1e1e", color: "#ddd", border: "1px solid #444", borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
                ↧ Cargar líneas pegadas
              </button>
              <button type="button" onClick={() => setApLineas([...apLineas, { cuenta: "", nombre: "", debe: "", haber: "" }])} className="t-02"
                style={{ background: "#1e1e1e", color: "#ddd", border: "1px solid #444", borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
                + Agregar línea
              </button>
            </div>

            {apLineas.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {apLineas.map((l, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <input value={l.cuenta} onChange={e => setApLineas(apLineas.map((x, j) => j === i ? { ...x, cuenta: e.target.value } : x))}
                      placeholder="Cuenta" style={{ ...inputS, width: 80, fontFamily: "monospace", fontSize: 13 }} />
                    <input value={l.nombre} onChange={e => setApLineas(apLineas.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x))}
                      placeholder="Nombre de la cuenta" style={{ ...inputS, flex: 1, minWidth: 140, fontSize: 13 }} />
                    <input value={l.debe} onChange={e => setApLineas(apLineas.map((x, j) => j === i ? { ...x, debe: e.target.value } : x))}
                      placeholder="Debe" style={{ ...inputS, width: 110, textAlign: "right", fontSize: 13, ...num }} />
                    <input value={l.haber} onChange={e => setApLineas(apLineas.map((x, j) => j === i ? { ...x, haber: e.target.value } : x))}
                      placeholder="Haber" style={{ ...inputS, width: 110, textAlign: "right", fontSize: 13, ...num }} />
                    <button type="button" onClick={() => setApLineas(apLineas.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 15 }}>✕</button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={guardarApertura} disabled={apGuardando} className="t-02"
                    style={{ background: apGuardando ? "#333" : "#e30613", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {apGuardando ? "Guardando…" : "Guardar apertura"}
                  </button>
                  <span style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
                    D {fmt(apLineas.reduce((s, l) => s + parseMonto(l.debe), 0))} · H {fmt(apLineas.reduce((s, l) => s + parseMonto(l.haber), 0))}
                  </span>
                  <span style={{ fontSize: 10, color: "#8a8a8a" }}>Guardar REEMPLAZA lo guardado para esa fecha de corte.</span>
                </div>
              </div>
            )}
            {apMsg && (
              <div style={{ marginTop: 8, fontSize: 12, color: apMsg.tipo === "ok" ? "#2ecc71" : apMsg.tipo === "warn" ? "#f39c12" : "#e74c3c" }}>{apMsg.texto}</div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ ...S.label, marginBottom: 8 }}>Apertura guardada ({apGuardadas.length} líneas)</div>
            {apGuardadas.length === 0 && <div style={{ fontSize: 12, color: "#8a8a8a" }}>Todavía no hay saldos de apertura — la contabilidad parte en cero.</div>}
            {apGuardadas.map(l => (
              <div key={`${String(l.fecha).slice(0, 10)}-${l.cuenta}`} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid #282828", flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, color: "#8a8a8a", flexShrink: 0 }}>{String(l.fecha).slice(0, 10)}</span>
                <span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700, flexShrink: 0 }}>{l.cuenta}</span>
                <span style={{ flex: 1, minWidth: 120, fontSize: 12, color: "#fff" }}>{l.nombreCuenta || l.cuenta}</span>
                <span style={{ fontSize: 11, color: "#b8b8b8", ...num }}>D {fmt(l.debe)} · H {fmt(l.haber)}</span>
                <button type="button" onClick={() => borrarLineaApertura(l)}
                  style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 13 }}>✕</button>
              </div>
            ))}
            {apGuardadas.length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 8, fontSize: 12, fontWeight: 700, ...num }}>
                {(() => {
                  const d = apGuardadas.reduce((s, l) => s + (l.debe || 0), 0);
                  const h = apGuardadas.reduce((s, l) => s + (l.haber || 0), 0);
                  return (
                    <>
                      <span style={{ color: "#fff" }}>D {fmt(d)} · H {fmt(h)}</span>
                      <span style={{ color: d === h ? "#2ecc71" : "#f39c12" }}>{d === h ? "✓ cuadrada" : `⚠ descuadre ${fmt(d - h)}`}</span>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bandeja de excepciones ── */}
      {vista === "excepciones" && (
        <div style={S.card}>
          <div style={{ ...S.label, marginBottom: 8 }}>Excepciones del motor — {periodo}</div>
          {excepciones.length === 0 && <div style={{ fontSize: 12, color: "#2ecc71" }}>✓ Sin excepciones: todos los documentos del período se contabilizaron.</div>}
          {/* Un AVISO no es una excepción: el documento SÍ quedó contabilizado y
              alguien tiene que revisarlo. Mezclarlos sin distinguir haría creer
              que la plata quedó fuera del libro cuando está adentro. */}
          {(() => {
            const avisos = excepciones.filter(x => x.clase === "aviso").length;
            const fuera = excepciones.length - avisos;
            if (!excepciones.length) return null;
            return (
              <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 8 }}>
                {fuera > 0 && <><b style={{ color: "#f39c12" }}>{fuera}</b> sin contabilizar</>}
                {fuera > 0 && avisos > 0 && " · "}
                {avisos > 0 && <><b style={{ color: "#3498db" }}>{avisos}</b> contabilizado{avisos === 1 ? "" : "s"} con observación</>}
              </div>
            );
          })()}
          {excepciones.map((x, i) => {
            // 28-ago: "ver documento" — el folio va en la glosa del motor
            // (ej. "Factura compra N° 4581 — …", "doc 61 folio 123 — …");
            // navega a la tabla de facturas con el buscador prellenado.
            const glosa = x.glosa || "";
            const mFolio = glosa.match(/(?:N°|folio)\s*(\d+)/i);
            const destino = /compra|DIN/i.test(glosa) ? "compras" : "ventas";
            const verDoc = x.origenTipo === "factura" && mFolio;
            return (
              <div key={x._id || i} style={{ padding: "8px 0", borderBottom: "1px solid #282828" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px", flexShrink: 0,
                    background: x.clase === "desfasado" ? "#12304a" : x.clase === "aviso" ? "#0d2035" : "#4a3a10",
                    color: x.clase === "desfasado" ? "#3498db" : x.clase === "aviso" ? "#5dade2" : "#f39c12" }}>
                    {x.clase === "desfasado" ? "DESFASADO" : x.clase === "aviso" ? "AVISO" : "EXCEPCIÓN"}
                  </span>
                  <span style={{ flex: 1, minWidth: 140, fontSize: 12, color: "#fff" }}>{x.glosa}</span>
                  <span style={{ fontSize: 10, color: "#8a8a8a" }}>{x.origenTipo}{x.numero ? ` · asiento #${x.numero}` : ""}</span>
                  {verDoc && (
                    <button type="button" className="t-02"
                      onClick={() => navigate(`/contabilidad?tab=${destino}&buscar=${mFolio[1]}`)}
                      style={{ background: "#0d2035", color: "#3498db", border: "1px solid #1a4a6e",
                        borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                      ver documento →
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: x.clase === "aviso" ? "#5dade2" : "#f39c12", marginTop: 3 }}>
                  {x.clase === "aviso" && <span style={{ color: "#8a8a8a" }}>se contabilizó igual · </span>}
                  {x.motivo}
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 10 }}>
            La bandeja se regenera con cada "Generar asientos del período". Un DESFASADO es un documento que cambió después de contabilizarse — requiere reversa manual. Un AVISO sí se contabilizó: el asiento está en el libro y lo que falta es completar o reclasificar un dato.
          </div>
        </div>
      )}
    </div>
  );
};

export default Libro;
